import { useMemo } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Menu, Loader, ActionIcon, CloseButton } from '@mantine/core'

import Tag from 'src/components/DataDisplay/Tag/Tag'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { RESOLVE_EMAIL_TEMPLATE } from 'src/graphql/emailTemplates.graphql'
import IconAlertCircle from 'src/icons/IconAlertCircle'
import IconChevronDown from 'src/icons/IconChevronDown'

interface EmailTemplatesProps {
  applicantId: string
  templatesList?: {
    emailTemplates?: {
      items?: Array<{
        emailTemplateId: string
        title: string
      }>
    }
  }
  templatesLoading?: boolean
  templatesError?: Error | null
  selectedTemplate?: {
    title: string | null
    emailTemplateId: string | null
  } | null
  onTemplateChange?: (
    template: {
      title: string | null
      emailTemplateId: string | null
    } | null
  ) => void
  onClear?: () => void
  onSelect?: (template: {
    subject: string
    body: string
    attachments?: unknown[]
    emailTemplateId?: string
  }) => void
}
const EmailTemplatesMenu = ({
  applicantId,
  templatesList,
  templatesLoading,
  templatesError,
  selectedTemplate,
  onTemplateChange,
  onSelect,
  onClear,
}: EmailTemplatesProps) => {
  const initialState = useMemo(
    () => ({ title: null, emailTemplateId: null }),
    []
  )

  const handleClear = () => {
    onTemplateChange?.(initialState)
    onClear?.()
  }

  const [resolveTemplate, { loading: resolving }] = useLazyQuery(
    RESOLVE_EMAIL_TEMPLATE,
    {
      onError: (error) => {
        // Try multiple paths to support both dev and production environments
        const errorMessage =
          error.graphQLErrors?.[0]?.extensions?.originalError?.message ||
          error.graphQLErrors?.[0]?.message ||
          error.message ||
          'Failed to resolve template'
        toast(errorMessage, 'error')
      },
    }
  )

  const handleItemClick = (item) => {
    resolveTemplate({
      variables: { emailTemplateId: item.emailTemplateId, applicantId },
    })
      .then((result) => {
        if (result.data) {
          onTemplateChange?.(item)
          onSelect?.({
            ...result.data.resolveEmailTemplate,
            emailTemplateId: item.emailTemplateId,
          })
        }
      })
      .catch(() => {
        toast('Failed to load email template', 'error')
      })
  }

  const renderItems = () => {
    if (templatesList?.emailTemplates?.items?.length) {
      return templatesList.emailTemplates.items.map((item) => (
        <Menu.Item
          key={item.emailTemplateId}
          onClick={() => handleItemClick(item)}
          className="truncate"
        >
          {item.title}
        </Menu.Item>
      ))
    }

    if (templatesLoading) {
      return (
        <Menu.Item>
          <Loader size={16} className="mx-auto w-full" color="gray" />
        </Menu.Item>
      )
    }

    if (templatesError) {
      return (
        <Menu.Item>
          <span className="flex flex-row items-center gap-2 text-doubleNickel-error-500">
            <IconAlertCircle className="h-4 w-4 fill-none stroke-doubleNickel-error-500" />
            Something went wrong.
          </span>
        </Menu.Item>
      )
    }

    return <Menu.Item>No templates found</Menu.Item>
  }

  return (
    <Menu
      width={250}
      shadow="md"
      classNames={{
        item: 'text-xs font-medium text-doubleNickel-gray-500',
      }}
      position="bottom-start"
      disabled={resolving}
    >
      <Menu.Target>
        <div className="flex flex-row items-center gap-1">
          {resolving ? (
            <Loader size={12} color="gray" />
          ) : (
            <>
              <Tag showCloseButton={false} variant="transparent">
                {!!selectedTemplate?.title && !resolving ? (
                  <div className="flex flex-row items-center gap-1 text-xs">
                    {selectedTemplate.title}
                    <CloseButton
                      onClick={(e) => {
                        e.stopPropagation()
                        handleClear()
                      }}
                      data-testid="clear-trigger"
                      size={'sm'}
                    />
                  </div>
                ) : (
                  <div className="flex flex-row items-center gap-1 text-xs">
                    Select a template
                    <ActionIcon
                      data-testid="select-trigger"
                      variant="subtle"
                      size="xs"
                      color="gray"
                    >
                      <IconChevronDown className="fill-none stroke-doubleNickel-gray-700" />
                    </ActionIcon>
                  </div>
                )}
              </Tag>
            </>
          )}
        </div>
      </Menu.Target>

      <Menu.Dropdown mah={400} className="overflow-y-auto">
        <Menu.Label>Available Templates</Menu.Label>
        {renderItems()}
      </Menu.Dropdown>
    </Menu>
  )
}

export default EmailTemplatesMenu
